import { generatePath } from 'react-router-dom';
import { apiClient, fetchAllPages } from '@/shared/api';
import type { FileDownloadFormat } from '@/shared/lib';
import type {
  CommentAuthorListResponse,
  CommentCreateRequest,
  CommentCreateResponse,
  CommentListResponse,
  CommentResponse,
  ConversationApplyValuesRequest,
  ConversationApplyValuesResponse,
  ConversationAttachmentSignedUrlResponse,
  ConversationChecklist,
  ConversationCreate,
  ConversationInfoResponse,
  ConversationRecordingFetchResponse,
  ConversationResponse,
  ConversationSignedUrlResponse,
  ClientListResponse,
  EmployeeListResponse,
  FileUploadResponse,
  PhraseCreateRequest,
  PhraseResponse,
  PhraseUpdateRequest,
} from './types';
import type { ConversationAttributeListResponse } from '@/entities/attributes/model/api/types';

// Re-export types for convenience
export type {
  CommentCreateRequest,
  CommentCreateResponse,
  CommentListResponse,
  CommentResponse,
  ConversationAttachmentSignedUrlResponse,
  ConversationCreate,
  ConversationInfoResponse,
  ConversationResponse,
  ConversationSignedUrlResponse,
};

/**
 * Ключ склейки уведомлений об отказе запросов одной карточки диалога. Карточка тянет сам диалог,
 * его транскрипт и комментарии разом, поэтому о диалоге, которого нет, она сообщает одним
 * уведомлением, а не тремя одинаковыми подряд. Ключ — по диалогу: беда соседней карточки своя.
 */
const conversationErrorToastKey = (id: number | string): string => `conversation:${id}`;

/**
 * Upload audio file per OpenAPI POST /conversation/audiofile
 * Загрузка аудиофайла в хранилище MinIO для дальнейшей обработки разговора.
 * Требуется разрешение ADMIN_CONVERSATION.
 * Допустимые форматы и размер файла — `CONVERSATION_AUDIO_FILE_TYPES` и `CONVERSATION_AUDIO_MAX_SIZE_MB`.
 *
 * `silentError` подавляет общее всплывающее уведомление: отказ по файлу раздел загрузки
 * показывает сам, называя файл.
 */
export const uploadConversationAudio = async (file: File): Promise<FileUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<FileUploadResponse>('/conversation/audiofile', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    silentError: true,
  });
  return data;
};

/**
 * Create a new conversation per OpenAPI PUT /conversation
 * Создание нового разговора с участниками, свойствами и метаданными.
 * Требуется разрешение ADMIN_CONVERSATION.
 * Позволяет создавать новых участников или обновлять существующих.
 *
 * `silentError` подавляет общее всплывающее уведомление: отказ по записи раздел загрузки
 * показывает сам, называя файл.
 */
export const createConversation = async (payload: ConversationCreate): Promise<ConversationResponse> => {
  const { data } = await apiClient.put<ConversationResponse>('/conversation', payload, { silentError: true });
  return data;
};

/**
 * Проставление отмеченным записям сотрудника, времени звонка и значений аналитических атрибутов
 * per OpenAPI POST /conversation/apply-values. Требуется разрешение ADMIN_CONVERSATION.
 * Все записи меняются одной транзакцией: при отказе не меняется ни одна.
 *
 * `silentError` подавляет общее всплывающее уведомление: причину отказа показывает раздел загрузки.
 */
export const applyConversationValues = async (
  payload: ConversationApplyValuesRequest,
): Promise<ConversationApplyValuesResponse> => {
  const { data } = await apiClient.post<ConversationApplyValuesResponse>('/conversation/apply-values', payload, {
    silentError: true,
  });
  return data;
};

/**
 * Fetch a conversation by ID per OpenAPI GET /conversation/{id}
 * Получение карточки диалога с атрибутами и участниками.
 * Требуется разрешение VIEW_CONVERSATION.
 * Применяется фильтрация по группам пользователя через SmartPredicateService.
 */
export const fetchConversation = async (
  id: number | string,
  options: { signal: AbortSignal },
): Promise<ConversationInfoResponse> => {
  const { data } = await apiClient.get<ConversationInfoResponse>(`/conversation/${id}`, {
    ...options,
    errorToastKey: conversationErrorToastKey(id),
  });
  return data;
};

/**
 * Fetch conversation transcript per OpenAPI GET /conversation/{id}/transcript
 * Получение полного транскрипта разговора в виде упорядоченного списка фраз
 * с информацией о говорящих и временных метках.
 * Включает все типы событий: PHRASE (фразы), INTERRUPTION (перебивания),
 * PAUSE (паузы), STOP_WORD (стоп-слова).
 * @param id - Conversation ID
 * @param anonymized - Вид текста реплик: true — анонимизированный, false — неанонимизированный
 *   (требует право VIEW_NON_ANONYMIZED_CONVERSATION на бэке)
 * @returns Promise with transcript phrases
 */
export const fetchConversationTranscript = async (
  id: number | string,
  anonymized: boolean,
  options: { signal: AbortSignal },
): Promise<PhraseResponse[]> => {
  const { data } = await apiClient.get<PhraseResponse[]>(
    generatePath('/conversation/:id/transcript', { id: id?.toString() }),
    { params: { anonymized }, signal: options.signal, errorToastKey: conversationErrorToastKey(id) },
  );
  return data;
};

/**
 * Добавление записи расшифровки per POST /conversation/{conversationId}/phrase:
 * реплики, перебивания или паузы; с `splitFrom` — вынос фрагмента текста существующей реплики.
 * Требуется разрешение EDIT_CONVERSATION_TRANSCRIPT.
 * @returns Созданная запись в анонимизированном виде, как элемент транскрипта
 */
export const createConversationPhrase = async (
  conversationId: number | string,
  data: PhraseCreateRequest,
): Promise<PhraseResponse> => {
  const { data: phrase } = await apiClient.post<PhraseResponse>(
    generatePath('/conversation/:conversationId/phrase', { conversationId: String(conversationId) }),
    data,
  );
  return phrase;
};

/**
 * Правка записи расшифровки per PATCH /conversation/{conversationId}/phrase/{phraseId}.
 * Требуется разрешение EDIT_CONVERSATION_TRANSCRIPT.
 * @returns Обновлённая запись в анонимизированном виде, как элемент транскрипта
 */
export const updateConversationPhrase = async (
  conversationId: number | string,
  phraseId: number,
  data: PhraseUpdateRequest,
): Promise<PhraseResponse> => {
  const { data: phrase } = await apiClient.patch<PhraseResponse>(
    generatePath('/conversation/:conversationId/phrase/:phraseId', {
      conversationId: String(conversationId),
      phraseId: String(phraseId),
    }),
    data,
  );
  return phrase;
};

/**
 * Удаление записи расшифровки per DELETE /conversation/{conversationId}/phrase/{phraseId};
 * реплика уходит вместе со своими перебиваниями и отметками стоп-слов.
 * Требуется разрешение EDIT_CONVERSATION_TRANSCRIPT.
 */
export const deleteConversationPhrase = async (conversationId: number | string, phraseId: number): Promise<void> => {
  await apiClient.delete(
    generatePath('/conversation/:conversationId/phrase/:phraseId', {
      conversationId: String(conversationId),
      phraseId: String(phraseId),
    }),
  );
};

/**
 * Fetch audio file for a conversation per OpenAPI GET /conversation/{id}/audiofile
 * Скачивание оригинального аудиофайла разговора из хранилища MinIO.
 * Поддерживает скачивание частями через заголовок Range для потокового воспроизведения.
 * @param id - Conversation ID
 * @returns Promise with audio blob
 */
export const fetchConversationAudioBlob = async (id: number | string): Promise<Blob> => {
  const { data } = await apiClient.get<Blob>(generatePath('/conversation/:id/audiofile', { id: id?.toString() }), {
    responseType: 'blob',
    silentError: true,
  });
  return data;
};

/**
 * Fetch audio file URL for a conversation
 * Скачивание оригинального аудиофайла разговора и создание URL для воспроизведения.
 * @param id - Conversation ID
 * @returns Promise with audio URL
 */
export const fetchConversationAudioUrl = async (id: number | string): Promise<string> => {
  const { data } = await apiClient.get<Blob>(generatePath('/conversation/:id/audiofile', { id: id?.toString() }), {
    responseType: 'blob',
    silentError: true,
  });
  return URL.createObjectURL(data);
};

/**
 * Fetch conversation checklist assessment per OpenAPI GET /conversation/{conversation_id}/checklist/{checklist_id}
 * Получение результатов оценки качества разговора по указанному чек-листу
 * с детализацией по блокам и критериям.
 * @param conversationId - Conversation ID
 * @param checklistId - Checklist ID
 * @returns Promise with checklist assessment
 */
export const fetchConversationChecklist = async (
  conversationId: number,
  checklistId: number,
): Promise<ConversationChecklist> => {
  const { data } = await apiClient.get<ConversationChecklist>(
    `/conversation/${conversationId}/checklist/${checklistId}`,
  );
  return data;
};

/**
 * Fetch conversations employees per OpenAPI GET /conversation/employee
 * Получение списка сотрудников (role=EMPLOYEE) с возможностью фильтрации
 * по имени/фамилии/отчеству и пагинации.
 * Фильтр поиска работает без учета регистра.
 */
export const fetchConversationsEmployees = async (
  params?: {
    /** Количество записей (1-100, default: 50) */
    limit?: number;
    /** Смещение (minimum: 0, default: 0) */
    offset?: number;
    /** Фильтр по имени/фамилии/отчеству */
    'employee-name'?: string | null;
    /** Точечная выборка по идентификаторам через запятую (не более 1000): пагинация к ней не применяется */
    'employee-id'?: string;
  },
  /** Сигнал отмены: отдельным аргументом, чтобы не попасть в query string вместе с params */
  signal?: AbortSignal,
): Promise<EmployeeListResponse> => {
  const { data } = await apiClient.get<EmployeeListResponse>('/conversation/employee', { params, signal });
  return data;
};

/**
 * Получение списка клиентов (role=CLIENT) по OpenAPI GET /conversation/client.
 * Поддерживает фильтрацию по имени/фамилии/отчеству и пагинацию.
 * Фильтр поиска работает без учёта регистра.
 */
export const fetchConversationsClients = async (
  params?: {
    /** Количество записей (1-1000, default: 50) */
    limit?: number;
    /** Смещение (minimum: 0, default: 0) */
    offset?: number;
    /** Фильтр по имени/фамилии/отчеству */
    'client-name'?: string | null;
    /** Точечная выборка по идентификаторам через запятую (не более 1000): пагинация к ней не применяется */
    'client-id'?: string;
  },
  /** Сигнал отмены: отдельным аргументом, чтобы не попасть в query string вместе с params */
  signal?: AbortSignal,
): Promise<ClientListResponse> => {
  const { data } = await apiClient.get<ClientListResponse>('/conversation/client', { params, signal });
  return data;
};

/**
 * Fetch conversations attributes per OpenAPI GET /conversation/attributes
 * Получение списка атрибутов для формирования таблицы с оцененными диалогами.
 * Требуется разрешение VIEW_CONVERSATION.
 * Поддерживается фильтрация по флагу удаления, группе атрибутов и пагинация.
 */
export const fetchConversationsAttributes = async (params?: {
  /** Фильтр по флагу удаления */
  'is-deleted'?: boolean | null;
  /** Количество записей (1-1000, default: 50) */
  limit?: number;
  /** Смещение (minimum: 0, default: 0) */
  offset?: number;
  /** Фильтр по кодам групп атрибутов (через запятую) */
  'attribute-group-code'?: string | null;
  /** Фильтр по типу атрибута (SYSTEM, COMPUTED_TRANSCRIBATION, COMPUTED_RAW, EXPERT_REPORT_RATING, ANALYTICS) */
  type?: string | string[] | null;
}): Promise<ConversationAttributeListResponse> => {
  const { data } = await apiClient.get<ConversationAttributeListResponse>('/conversation/attributes', { params });
  return data;
};

/** Наибольшее окно выдачи атрибутов для таблицы диалогов, которое принимает сервер */
const CONVERSATION_ATTRIBUTES_MAX_LIMIT = 1000;

/** Весь набор атрибутов для таблицы диалогов — окнами выдачи до последнего атрибута ({@link fetchAllPages}) */
export const fetchAllConversationsAttributes = async (
  params?: Omit<NonNullable<Parameters<typeof fetchConversationsAttributes>[0]>, 'limit' | 'offset'>,
): Promise<ConversationAttributeListResponse> => {
  const { items, total } = await fetchAllPages(async (offset, limit) => {
    const page = await fetchConversationsAttributes({ ...params, limit, offset });
    return { items: page.attributes, total: page.total };
  }, CONVERSATION_ATTRIBUTES_MAX_LIMIT);

  return { total, attributes: items };
};

/**
 * Fetch comments for a conversation per OpenAPI GET /conversation/{id}/comment
 * Получение списка комментариев к диалогу с пагинацией.
 * Сортировка по дате создания (сначала новые).
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const fetchConversationComments = async (
  id: number | string,
  params?: {
    /** Количество записей (1-100, default: 50) */
    limit?: number;
    /** Смещение (minimum: 0, default: 0) */
    offset?: number;
  },
): Promise<CommentListResponse> => {
  const { data } = await apiClient.get<CommentListResponse>(`/conversation/${id}/comment`, {
    params: { limit: params?.limit, offset: params?.offset },
    errorToastKey: conversationErrorToastKey(id),
  });
  return data;
};

/**
 * Fetch comment authors per OpenAPI GET /conversation/comment/authors
 * Список пользователей, оставивших хотя бы один комментарий к диалогу — наполняет
 * выпадающий список выбора автора в поиске по комментариям.
 * Поддерживает фильтр по частям имени (без учёта регистра) и пагинацию.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const fetchConversationCommentAuthors = async (params?: {
  /** Фильтр по частям имени автора (имя/отчество/фамилия) */
  name?: string | null;
  /** Количество записей (1-100, default: 50) */
  limit?: number;
  /** Смещение (minimum: 0, default: 0) */
  offset?: number;
}): Promise<CommentAuthorListResponse> => {
  const { data } = await apiClient.get<CommentAuthorListResponse>('/conversation/comment/authors', { params });
  return data;
};

/**
 * Create a comment for a conversation per OpenAPI POST /conversation/{id}/comment
 * Создание нового комментария к диалогу.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const createConversationComment = async (
  id: number | string,
  payload: CommentCreateRequest,
): Promise<CommentCreateResponse> => {
  const { data } = await apiClient.post<CommentCreateResponse>(`/conversation/${id}/comment`, payload);
  return data;
};

/**
 * Update a comment per OpenAPI PUT /conversation/comment/{id}
 * Редактирование комментария. Только автор комментария может его редактировать.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const updateConversationComment = async (
  id: number | string,
  payload: CommentCreateRequest,
): Promise<CommentResponse> => {
  const { data } = await apiClient.put<CommentResponse>(`/conversation/comment/${id}`, payload);
  return data;
};

/**
 * Delete a comment per OpenAPI DELETE /conversation/comment/{id}
 * Удаление комментария. Только автор комментария может его удалить.
 * Требуется разрешение VIEW_CONVERSATION.
 */
export const deleteConversationComment = async (id: number | string): Promise<void> => {
  await apiClient.delete(`/conversation/comment/${id}`);
};

/**
 * Run speech analytics for a conversation per OpenAPI POST /conversation/{id}/speech-analytics
 * Запускает речевую аналитику по звонку. Метод определяет перебивания, паузы, стоп-слова
 * и вычисляет метрики речи для сохранения в атрибуты диалога.
 * Требуется разрешение ADMIN_CONVERSATION.
 */
export const runSpeechAnalytics = async (id: number | string): Promise<void> => {
  await apiClient.post(`/conversation/${id}/speech-analytics`);
};

/**
 * Скачивание транскрипции диалога по выбранному шаблону
 * GET /conversation/{id}/transcript/download?templateId={templateId}&format={format}
 * Состав документа задаёт шаблон, формат файла выбирает пользователь; формат обязателен.
 * Требуется разрешение VIEW_CONVERSATION.
 *
 * `preserveParamsCase` — контракт эндпоинта задаёт условие как `templateId`, а не kebab-case.
 * @param id - Conversation ID
 * @param templateId - Шаблон транскрипции
 * @param format - Формат получаемого файла
 * @returns Promise with file Blob
 */
export const downloadConversationTranscript = async (
  id: number | string,
  templateId: number,
  format: FileDownloadFormat,
): Promise<Blob> => {
  const { data } = await apiClient.get<Blob>(
    generatePath('/conversation/:id/transcript/download', { id: id?.toString() }),
    {
      params: { templateId, format },
      preserveParamsCase: true,
      responseType: 'blob',
    },
  );
  return data;
};

/**
 * Fetch signed URL parameters for a conversation per OpenAPI GET /conversation/{id}/signed-url
 * Получение параметров подписанной ссылки для доступа к ресурсу разговора.
 * @param id - Conversation ID
 * @returns Promise with signed URL response
 */
export const fetchConversationSignedUrl = async (id: number | string): Promise<ConversationSignedUrlResponse> => {
  const { data } = await apiClient.get<ConversationSignedUrlResponse>(
    generatePath('/conversation/:id/signed-url', { id: id?.toString() }),
    {
      silentError: true,
    },
  );
  return data;
};

/**
 * Повторный запрос записи диалога у интеграции-источника (POST /conversation/{id}/recording-fetch).
 * Заказывает получение заново и после неудачи; идущее получение не удваивает. Отказ `400` —
 * получить запись неоткуда.
 */
export const requestConversationRecordingFetch = async (
  id: number | string,
): Promise<ConversationRecordingFetchResponse> => {
  const { data } = await apiClient.post<ConversationRecordingFetchResponse>(
    generatePath('/conversation/:id/recording-fetch', { id: id.toString() }),
  );
  return data;
};

/**
 * Получение подписанной ссылки на вложение диалога per spec api/conversation/attachment-signed-url.md
 * (GET /conversation/attachment/{attachmentId}/signed-url).
 * Возвращает подписанную ссылку на файл из внутреннего хранилища (MinIO).
 * Применимо только для вложений с isExternalUrl=false.
 */
export const fetchConversationAttachmentSignedUrl = async (
  attachmentId: number | string,
): Promise<ConversationAttachmentSignedUrlResponse> => {
  const { data } = await apiClient.get<ConversationAttachmentSignedUrlResponse>(
    generatePath('/conversation/attachment/:attachmentId/signed-url', {
      attachmentId: attachmentId?.toString(),
    }),
  );
  return data;
};
